/**
 * Local store for the two replicated object sets.
 *
 * Both sets are grow-only. [LinkPreimage]s are deduplicated by projection and exposed as a
 * [ProjectedLinkRecordResolver] so the projection/observation layer can rebuild local Link structure. [SignedCommitment]
 * attestations are deduplicated by head projection and indexed by credential, retaining every head an actor signs so
 * forks remain observable.
 *
 * [InMemoryReplicatedEventStore] is the portable default; hosts may implement this same synchronous indexed surface over
 * native durable storage. Persistence strategy stays outside the replicated-set abstraction.
 */

package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.data.ProjectedLinkRecord
import com.oasisfeng.invaluable.data.ProjectedLinkRecordResolver
import com.oasisfeng.invaluable.data.memoizing
import kotlinx.io.bytestring.ByteString

/** Read/append surface over the local preimage, attestation, and sealed-content sets. */
interface ReplicatedEventStore {

    /** Projection scheme that every stored projection must belong to. */
    val projector: LinkProjector

    /** View of the stored preimages as a resolver for the projection/observation layer. */
    fun resolver(): ProjectedLinkRecordResolver

    fun containsPreimage(projection: LinkProjection): Boolean
    fun containsAttestation(head: LinkProjection): Boolean

    /** Whether a sealed ciphertext with this digest is already held. The digest is [SealedContent.ciphertextDigest]. */
    fun containsSealed(ciphertextDigest: ByteString): Boolean

    /** Attestation for a head projection, or null if no such head has been accepted. */
    fun attestation(head: LinkProjection): SignedCommitment?

    /** Accepted head projections for one credential projection; more than one means an observable fork. */
    fun headsOf(credential: LinkProjection): Set<LinkProjection>

    /**
     * Every known credential's accepted heads — the per-actor commitment frontier.
     *
     * This is the compact digest a sync layer advertises on connect: O(actors) rather than O(events). A peer compares it
     * against its own frontier and walks each chain's parent links back to the divergence point to transfer only the
     * missing suffix. A credential mapped to more than one head is an observable fork.
     */
    fun frontier(): Map<LinkProjection, Set<LinkProjection>>

    /**
     * How many times something has been erased from this store — bumped by [removePreimage] / [removeSealed], never by
     * an append.
     *
     * Grow-only is what lets a reader remember how far it has read and later take only what came after. Erasure is the
     * one act that breaks it: after one, a remembered position names different records. A store that never forgets can
     * answer 0 forever; one that does must make it observable, since nothing else about the read surface reveals it
     * (see [DurableCursor]).
     */
    val erasures: Int

    fun preimages(): Sequence<ProjectedLinkRecord>
    fun attestations(): Sequence<SignedCommitment>

    /**
     * Every held sealed ciphertext. These are the custody-bearing carriers (§20.1): a [CapabilityResolver] is built over
     * the subset whose [contentProjection][SealedContent.contentProjection] a resolution needs, and a custodian proves
     * possession of any of them via [SealedContent.ciphertextDigest] without reading.
     */
    fun sealedContents(): Sequence<SealedContent>

    /** All held seals naming [contentProjection]; more than one means the same content sealed under distinct keys. */
    fun sealedFor(contentProjection: LinkProjection): Sequence<SealedContent> =
        sealedContents().filter { it.contentProjection == contentProjection }

    /** Append a preimage; returns false if an equal projection was already present. */
    fun appendPreimage(record: ProjectedLinkRecord): Boolean

    /** Append an attestation; returns false if that head projection was already present. */
    fun appendAttestation(attestation: SignedCommitment): Boolean

    /**
     * Append a sealed ciphertext; returns false if an equal one — same ciphertext **and** same claimed
     * [contentProjection][SealedContent.contentProjection] — was already present.
     *
     * Seals are *not* deduplicated by ciphertext digest alone. [contentProjection] is an unverified index hint (a keyless
     * observer cannot check the ciphertext opens to it; integrity is re-checked only at read), so deduping by digest would
     * let a first-arriving wrong hint monopolize a ciphertext's single slot and starve the honest `(correctProjection,
     * ciphertext)` seal — a poisoning that would hide the content from a legitimate key-holder. Keying by the
     * `(ciphertext, claimed projection)` pair lets every claimed projection for one ciphertext coexist, so
     * [sealedFor] always finds the honest pairing regardless of arrival order.
     */
    fun appendSealed(sealed: SealedContent): Boolean

    /**
     * Forget one preimage by projection — the store side of local forgetting (roadmap M10); returns true if one was
     * present and removed.
     *
     * Only a commitment's **content** closure should ever be forgotten: a forgotten body degrades to a missing
     * observation for this node while the commitment's spine (header) and [SignedCommitment] attestation stay, so the
     * commitment remains permanently attributable — its existence is required for journal validity and is never removed
     * (there is deliberately no `removeAttestation`). Forgetting is **local and reversible**: re-appending the same
     * preimage (e.g. re-synced from a peer that still holds it) restores it — "re-awakening the forgotten memory". A
     * content truly dies only once every node has forgotten it, leaving its content-digest dangling for all.
     */
    fun removePreimage(projection: LinkProjection): Boolean

    /**
     * Forget one sealed ciphertext by digest — the custody side of local forgetting; returns true if one was removed.
     *
     * Like [removePreimage] this is local and reversible: a re-synced seal restores it. Dropping a ciphertext is what
     * makes a custodian's possession proof start to fail, so its custody attribution lapses (§20.1) — but the
     * commitment's spine and attestation are untouched, so it stays attributable; nothing is ever globally deleted.
     */
    fun removeSealed(ciphertextDigest: ByteString): Boolean
}

/** In-memory [ReplicatedEventStore] with no persistence. */
class InMemoryReplicatedEventStore(override val projector: LinkProjector) : ReplicatedEventStore {

    private val preimagesByProjection = LinkedHashMap<LinkProjection, ProjectedLinkRecord>()
    private val attestationsByHead = LinkedHashMap<LinkProjection, SignedCommitment>()
    private val headsByCredential = LinkedHashMap<LinkProjection, MutableSet<LinkProjection>>()
    // Keyed by the (ciphertext digest, claimed content projection) pair, not the digest alone: the projection is an
    // unverified hint, so an attacker-supplied wrong pairing must not be able to evict or block the honest one (see
    // [ReplicatedEventStore.appendSealed]). Insertion-ordered so eviction/iteration is deterministic.
    private val sealedByKey = LinkedHashMap<Pair<ByteString, LinkProjection>, SealedContent>()

    private val SealedContent.key get() = ciphertextDigest to contentProjection

    /**
     * One memoizing resolver over the whole store, rather than a fresh one per call.
     *
     * Rebuilding a Link walks its entire projection DAG, and consecutive commitments in an actor's log chain through
     * one another — so resolving a log head-by-head re-walks the same ancestry every time, quadratic in the log. This
     * store's records are grow-only *except* for the deliberate erasures below, which drop the memo: within any window
     * where nothing was forgotten, a resolution stays exactly as true as when it was first computed.
     *
     * The memo covers only what this bare resolver can see. A resolver decorated with capabilities
     * ([CapabilityResolver]) is a different, wider view, and [com.oasisfeng.invaluable.data.resolveLink] only ever
     * writes into the memo of the resolver it was handed — so sealed material opened with a key can never leak into
     * this one and make a locked post look readable.
     */
    private var memo = ProjectedLinkRecordResolver { preimagesByProjection[it] }.memoizing()

    override var erasures: Int = 0
        private set

    override fun resolver(): ProjectedLinkRecordResolver = memo

    /**
     * Forget every memoized resolution: something the memo may have been built on is no longer visible.
     *
     * The same act also renumbers the insertion-ordered sets for anyone reading them by position, so [erasures] moves
     * here too — the two are the same fact seen from two sides.
     */
    private fun forgetResolutions() {
        memo = ProjectedLinkRecordResolver { preimagesByProjection[it] }.memoizing()
        erasures++
    }

    override fun containsPreimage(projection: LinkProjection): Boolean = preimagesByProjection.containsKey(projection)
    override fun containsAttestation(head: LinkProjection): Boolean = attestationsByHead.containsKey(head)
    override fun containsSealed(ciphertextDigest: ByteString): Boolean =
        sealedByKey.keys.any { it.first == ciphertextDigest }
    override fun attestation(head: LinkProjection): SignedCommitment? = attestationsByHead[head]
    override fun headsOf(credential: LinkProjection): Set<LinkProjection> = tipsOf(headsByCredential[credential].orEmpty())
    override fun frontier(): Map<LinkProjection, Set<LinkProjection>> = headsByCredential.mapValues { tipsOf(it.value) }

    /**
     * Reduce a credential's accepted heads to its advertisable frontier: the tips that are not the parent of any other
     * accepted head. A linear chain collapses to its single latest head — keeping the wire digest O(actors), not
     * O(events) — while a fork keeps every divergent tip. Computed at read time, so it is independent of the order
     * attestations were accepted in.
     */
    private fun tipsOf(heads: Set<LinkProjection>): Set<LinkProjection> {
        if (heads.size <= 1) return heads.toSet()
        val resolver = resolver()
        val superseded = heads.mapNotNullTo(mutableSetOf()) { resolver.recordFor(it)?.parent }
        return heads.filterTo(LinkedHashSet()) { it !in superseded }
    }

    override fun preimages(): Sequence<ProjectedLinkRecord> = preimagesByProjection.values.asSequence()
    override fun attestations(): Sequence<SignedCommitment> = attestationsByHead.values.asSequence()
    override fun sealedContents(): Sequence<SealedContent> = sealedByKey.values.asSequence()

    override fun appendPreimage(record: ProjectedLinkRecord): Boolean {
        if (preimagesByProjection.containsKey(record.projection)) return false
        preimagesByProjection[record.projection] = record
        return true
    }

    override fun appendAttestation(attestation: SignedCommitment): Boolean {
        if (attestationsByHead.containsKey(attestation.head)) return false
        attestationsByHead[attestation.head] = attestation
        headsByCredential.getOrPut(attestation.credential) { LinkedHashSet() }.add(attestation.head)
        return true
    }

    override fun appendSealed(sealed: SealedContent): Boolean {
        if (sealedByKey.containsKey(sealed.key)) return false
        sealedByKey[sealed.key] = sealed
        return true
    }

    override fun removePreimage(projection: LinkProjection): Boolean =
        (preimagesByProjection.remove(projection) != null).also { if (it) forgetResolutions() }

    // Forget a ciphertext: drop every seal carrying it, whatever projection each one claims. True if any was removed.
    override fun removeSealed(ciphertextDigest: ByteString): Boolean =
        sealedByKey.keys.filter { it.first == ciphertextDigest }.toList()
            .onEach { sealedByKey.remove(it) }.isNotEmpty().also { if (it) forgetResolutions() }
}
